import React, { Component } from "react";
import "./OperatorSearch.css";
import { SearchForm } from "./SearchForm";
import { SearchResults } from "./SearchResults";
import queryString from "query-string";
import { navigate } from "@reach/router";
import { FormattedMessage } from "react-intl";
import { parseLang, constructApiUrl, isTermValid, isTermListValid } from "../utils";
import { Helmet } from "react-helmet";
import { CompetencyDescriptions } from "../components/CompetencyDescriptions";

export class OperatorSearch extends Component {
  initialState = {
    selectedMunicipalities: [],
    selectedOperatingRights: [],
    page: 0,
    pageSize: 10,
    searchText: "",
    searchPersonText: "",
    sortColumn: "nimi",
    sortDirection: "asc",
    totalRows: 0,
    searchResults: null,
    searching: false,
    searchError: false,
  };

  constructor(props) {
    super(props);
    this.state = { ...this.initialState };
  }

  componentDidMount() {
    const { location } = this.props;
    const query = queryString.parse(location.search);

    // Set initial local state from query params
    this.setState(
      {
        selectedMunicipalities: query.m || [],
        selectedOperatingRights: query.o || [],
        page: Number(query.p) || 0,
        searchText: query.q || "",
        searchPersonText: query.qp || "",
        sortColumn: query.sc || "nimi",
        sortDirection: query.sd || "asc",
      },
      () => {
        if (this.isFormValid()) {
          this.fetch();
        }
      }
    );
  }

  resetForm = () => {
    this.setState(this.initialState);
    this.resetQueryString();
  };

  isFormValid = () => {
    return (
      isTermListValid(this.state.searchPersonText) ||
      isTermValid(this.state.searchText) ||
      this.state.selectedOperatingRights.length ||
      this.state.selectedMunicipalities.length
    );
  };

  buildQueryString = () => {
    return queryString.stringify({
      term: this.state.searchText,
      person: this.state.searchPersonText,
      page: this.state.page,
      pageSize: this.state.pageSize,
      municipality: this.state.selectedMunicipalities,
      operationRight: this.state.selectedOperatingRights,
      order: `${!this.state.sortColumn || this.state.sortDirection === "asc" ? "" : "-"}${this.state.sortColumn}`,
    });
  };

  // updates new query parameters to current url
  navigateWithQuery = (query) => {
    const { location, uri } = this.props;
    const stringified = queryString.stringify({
      ...queryString.parse(location.search),
      ...query,
    });
    navigate(`${uri}?${stringified}`, { replace: true });
  };

  municipalitySelected = (nextValues) => {
    const queryValues = nextValues.map((value) => value.value);
    this.setState({ selectedMunicipalities: queryValues }, () => {
      this.navigateWithQuery({ m: queryValues });
    });
  };

  operatingRightSelected = (nextValues) => {
    const queryValues = nextValues.map((value) => value.value);
    this.setState({ selectedOperatingRights: queryValues }, () => {
      this.navigateWithQuery({ o: queryValues });
    });
  };

  changePage = (nextPage) => {
    this.setState({ page: nextPage }, () => {
      this.navigateWithQuery({ p: nextPage });
      this.fetch();
    });
  };

  setSearchText = (event) => {
    const searchText = event.currentTarget.value;
    this.setState({ searchText }, () => {
      this.navigateWithQuery({ q: searchText });
    });
  };

  setSearchPersonText = (event) => {
    const searchPersonText = event.currentTarget.value;
    this.setState({ searchPersonText }, () => {
      this.navigateWithQuery({ qp: searchPersonText });
    });
  };

  resetQueryString = () => {
    this.navigateWithQuery({
      qp: [],
      q: [],
      sd: [],
      sc: [],
      m: [],
      o: [],
      p: [],
    });
  };

  sortChanged = (sortColumn) => {
    const sortDirection = this.state.sortDirection === "asc" ? "desc" : "asc";
    if (this.state.sortColumn === sortColumn) {
      this.setState({ sortDirection }, () => {
        this.navigateWithQuery({ sd: sortDirection });
        this.fetch();
      });
    } else {
      this.setState({ sortColumn, sortDirection }, () => {
        this.navigateWithQuery({ sc: sortColumn, sd: sortDirection });
        this.fetch();
      });
    }
  };

  fetch = async (setPage) => {
    await this.setState((state) => ({
      ...state,
      page: Number.isInteger(setPage) ? setPage : state.page,
      searching: !state.searching,
      searchResults: null,
    }));

    if (Number.isInteger(setPage)) this.navigateWithQuery({ p: setPage });

    const apiUrl = process.env.REACT_APP_SEARCH_OPERATORS_URL;

    try {
      const query = this.buildQueryString();
      const response = await fetch(constructApiUrl(apiUrl, query));
      const responseJSON = await response.json();
      if (response.status !== 200) {
        this.setState({
          searchResults: [],
          totalRows: 0,
          searching: false,
          searchError: responseJSON,
        });
        return false;
      }
      const { totalRows, results } = responseJSON;
      this.setState({
        searchResults: results,
        totalRows: totalRows,
        searching: false,
        searchError: false,
      });
    } catch (error) {
      this.setState({
        searchResults: [],
        totalRows: 0,
        searching: false,
        searchError: "Unknown",
      });
    }
  };

  render() {
    const queryLang = parseLang(this.props.location.search);
    const { location } = this.props;

    return (
      <div className="OperatorSearch">
        <FormattedMessage tagName="h1" id="operatorSearch.header" />
        <h2>
          <CompetencyDescriptions locale={queryLang} location={location} />
        </h2>
        <FormattedMessage id="operatorSearch.header">
          {(operatorTitle) => <Helmet title={operatorTitle} />}
        </FormattedMessage>

        <SearchForm
          fetch={this.fetch}
          isFormValid={this.isFormValid}
          locale={queryLang}
          searchText={this.state.searchText}
          searchPersonText={this.state.searchPersonText}
          selectedMunicipalities={this.state.selectedMunicipalities}
          selectedOperatingRights={this.state.selectedOperatingRights}
          updateMunicipalities={this.municipalitySelected}
          updateOperatingRights={this.operatingRightSelected}
          updateSearchText={this.setSearchText}
          updateSearchPersonText={this.setSearchPersonText}
          searching={this.state.searching}
          resetForm={this.resetForm}
        />
        <SearchResults
          changePage={this.changePage}
          currentPage={this.state.page}
          pageSize={this.state.pageSize}
          results={this.state.searchResults}
          totalRows={this.state.totalRows}
          searching={this.state.searching}
          searchError={this.state.searchError}
          sortColumn={this.state.sortColumn}
          sortDirection={this.state.sortDirection}
          updateSort={this.sortChanged}
          locale={queryLang}
        />
      </div>
    );
  }
}


// WEBPACK FOOTER //
// ./src/OperatorSearch/OperatorSearch.js